import type { SimpleTableConfig } from "../UniversalCRMTable/types";

/**
 * Build default form values from config and initial values.
 * Pure function: config + initialValues + appUserId → defaults object.
 */
export function buildDefaultValues(
  config: SimpleTableConfig<any>,
  initialValues: Record<string, any> = {},
  appUserId?: string
): Record<string, any> {
  const defaults: any = { ...initialValues };

  // Initialize fields based on their type configuration
  Object.entries(config.fields).forEach(([fieldName, field]) => {
    const currentValue = defaults[fieldName];

    switch (field.type) {
      case "multi-user":
        // Handle GraphQL relation data - extract UUIDs from array of objects
        if (Array.isArray(currentValue)) {
          defaults[fieldName] = currentValue
            .map((item: any) => {
              if (typeof item === "string") return item;
              if (typeof item === "object" && item?.id) return item.id;
              return null;
            })
            .filter(Boolean);
        } else if (currentValue) {
          defaults[fieldName] = [currentValue];
        } else {
          defaults[fieldName] = [];
        }
        // Default "assign to me" only for primary assignee-style fields — not engineer_ids etc.
        if (appUserId && defaults[fieldName].length === 0 && fieldName === "assigned_users") {
          defaults[fieldName] = [appUserId];
        }
        break;

      case "tags":
        // Initialize as empty array if not set
        if (!Array.isArray(currentValue)) {
          defaults[fieldName] = currentValue ? [currentValue] : [];
        }
        break;

      case "boolean":
        // Initialize as false if not set
        if (currentValue === undefined || currentValue === null) {
          defaults[fieldName] = false;
        }
        break;

      case "number":
        // Initialize as undefined if not set or if NaN (let form handle empty values)
        if (currentValue === "" || currentValue === null || isNaN(currentValue)) {
          defaults[fieldName] = undefined;
        }
        break;

      case "due-date":
      case "date":
      case "datetime":
      case "time":
      case "date-worked":
        // Initialize as undefined if not set, unless autoFill is enabled
        if (!currentValue) {
          if (field.autoFill) {
            // Auto-fill with current date/time based on field type
            const now = new Date();
            switch (field.type) {
              case "date":
                defaults[fieldName] = now.toISOString().split("T")[0]; // YYYY-MM-DD
                break;
              case "datetime":
                defaults[fieldName] = now.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
                break;
              case "time":
                defaults[fieldName] = now.toTimeString().slice(0, 5); // HH:MM
                break;
              case "due-date":
                // For due-date, auto-fill with current date when autoFill is enabled
                defaults[fieldName] = now.toISOString().split("T")[0]; // YYYY-MM-DD
                break;
              case "date-worked":
                // For date-worked, auto-fill with current date when autoFill is enabled
                defaults[fieldName] = now.toISOString().split("T")[0]; // YYYY-MM-DD
                break;
              default:
                defaults[fieldName] = undefined;
            }
          } else {
            defaults[fieldName] = undefined;
          }
        }
        break;

      case "text":
      case "textarea":
        // Initialize as empty string if not set, unless autoFill is enabled
        if (currentValue === undefined || currentValue === null) {
          if (field.autoFill) {
            // Auto-fill with current values based on field name or type
            if (fieldName.includes("created") || fieldName.includes("timestamp")) {
              defaults[fieldName] = new Date().toISOString();
            } else {
              defaults[fieldName] = "";
            }
          } else {
            defaults[fieldName] = "";
          }
        }
        break;

      case "select":
        // Initialize with first option or empty string
        if (!currentValue && field.options && field.options.length > 0) {
          defaults[fieldName] = field.options[0].value;
        } else if (!currentValue) {
          defaults[fieldName] = "";
        }
        break;

      case "client-select":
        // Handle GraphQL relation data - extract UUID from object
        if (currentValue && typeof currentValue === "object" && currentValue.id) {
          defaults[fieldName] = currentValue.id;
        } else if (currentValue && typeof currentValue === "string") {
          defaults[fieldName] = currentValue;
        } else {
          // Check if we have the contact data under "contacts" key (GraphQL relation)
          const contactsKey = fieldName.replace("_id", "s"); // contact_id -> contacts
          if (
            defaults[contactsKey] &&
            typeof defaults[contactsKey] === "object" &&
            defaults[contactsKey].id
          ) {
            defaults[fieldName] = defaults[contactsKey].id;
          } else {
            defaults[fieldName] = "";
          }
        }
        break;

      case "ticket-select":
        // Handle GraphQL relation data - extract UUID from object
        if (currentValue && typeof currentValue === "object" && currentValue.id) {
          defaults[fieldName] = currentValue.id;
        } else if (currentValue && typeof currentValue === "string") {
          defaults[fieldName] = currentValue;
        } else {
          // Check if we have the ticket data under "tickets" or "ticket" key (GraphQL relation)
          const ticketsKey = fieldName.replace("_id", "s"); // ticket_id -> tickets
          const ticketKey = fieldName.replace("_id", ""); // ticket_id -> ticket
          if (
            defaults[ticketsKey] &&
            typeof defaults[ticketsKey] === "object" &&
            defaults[ticketsKey].id
          ) {
            defaults[fieldName] = defaults[ticketsKey].id;
          } else if (
            defaults[ticketKey] &&
            typeof defaults[ticketKey] === "object" &&
            defaults[ticketKey].id
          ) {
            defaults[fieldName] = defaults[ticketKey].id;
          } else {
            defaults[fieldName] = "";
          }
        }
        break;

      case "user":
        // Initialize with current user if not set
        if (!currentValue) {
          defaults[fieldName] = appUserId || "";
        }
        break;

      case "array":
        // Initialize as empty array if not set
        if (!currentValue) {
          defaults[fieldName] = [];
        }
        break;

      case "json":
        // Staff/hard-cost line items: initialize as empty array
        if ((field as { lineItemsType?: string }).lineItemsType) {
          if (!Array.isArray(currentValue)) {
            defaults[fieldName] = [];
          }
        }
        break;

      default:
        // Handle any _id fields that might contain GraphQL relation data
        if (
          fieldName.endsWith("_id") &&
          currentValue &&
          typeof currentValue === "object" &&
          currentValue.id
        ) {
          defaults[fieldName] = currentValue.id;
        } else if (fieldName.endsWith("_id")) {
          // Check if we have the relation data under plural key (e.g., contact_id -> contacts)
          const relationKey = fieldName.replace("_id", "s"); // contact_id -> contacts, user_id -> users
          if (
            defaults[relationKey] &&
            typeof defaults[relationKey] === "object" &&
            defaults[relationKey].id
          ) {
            defaults[fieldName] = defaults[relationKey].id;
          }
        }
        // For other field types, keep existing value or set to undefined
        if (currentValue === undefined || currentValue === null) {
          defaults[fieldName] = undefined;
        }
        break;
    }
  });

  // Auto-populate user_id if available and not already set (most entities need this)
  if (appUserId && !defaults.user_id) {
    defaults.user_id = appUserId;
  }

  return defaults;
}
